<template>
    <div class="max-w-6xl mx-auto pb-6 space-y-6">
        <Loading :overlay="true" overlay-text="gerando relatório..." :show="loadingReport" />

        <h1 class="text-2xl font-bold text-gray-800">Avaliações</h1>

        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
                <p class="text-sm text-gray-500">{{ totalEvaluations }} {{ totalEvaluations === 1 ? 'avaliação' : 'avaliações' }}</p>
                <p class="text-xs text-gray-400">Busque por nome, email, telefone ou documento do paciente.</p>
            </div>
        </div>

        <div class="relative">
            <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
            </span>
            <input
                v-model="searchInput"
                type="text"
                placeholder="Buscar por nome, email, telefone ou CPF/CNPJ..."
                class="input input-bordered w-full pl-10 pr-10 text-sm"
                @keyup.enter="applySearch"
            />
            <button
                v-if="searchInput"
                type="button"
                class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                title="Limpar"
                @click="clearSearch"
            >&#10005;</button>
        </div>

        <div v-if="activeSearch" class="flex flex-wrap items-center gap-2 text-xs text-gray-500 -mt-3">
            <span>Filtrado por: <strong class="text-gray-700">"{{ activeSearch }}"</strong></span>
            <button type="button" class="text-cyan-600 hover:text-cyan-700 font-semibold" @click="clearSearch">Limpar filtro &#10005;</button>
        </div>

        <div v-if="loading" class="space-y-3">
            <div v-for="i in 6" :key="i" class="bg-white border border-gray-100 rounded-xl shadow-sm p-4">
                <div class="flex items-center gap-3 mb-4">
                    <div class="skeleton w-10 h-10 rounded-full shrink-0"></div>
                    <div class="flex-1 space-y-2">
                        <div class="skeleton h-4 w-40 rounded"></div>
                        <div class="skeleton h-3 w-56 rounded"></div>
                    </div>
                </div>
                <div class="grid gap-3 sm:grid-cols-3">
                    <div class="space-y-2">
                        <div class="skeleton h-3 w-20 rounded"></div>
                        <div class="skeleton h-4 w-28 rounded"></div>
                    </div>
                    <div class="space-y-2">
                        <div class="skeleton h-3 w-24 rounded"></div>
                        <div class="skeleton h-4 w-32 rounded"></div>
                    </div>
                    <div class="space-y-2">
                        <div class="skeleton h-3 w-16 rounded"></div>
                        <div class="skeleton h-4 w-16 rounded"></div>
                    </div>
                </div>
            </div>
        </div>

        <Alert
            :text="activeSearch ? 'Nenhuma avaliação encontrada para esta busca' : 'Ainda não existem avaliações cadastradas'"
            :show="!evaluations.length && !loading"
        />

        <div v-if="!loading && evaluations.length" class="space-y-3">
            <div
                v-for="record in evaluations"
                :key="record.id"
                class="bg-white border border-gray-100 rounded-xl shadow-sm p-4 transition-colors"
                :class="canOpenRecord(record) ? 'hover:border-cyan-200 hover:bg-gray-50 cursor-pointer' : 'opacity-75'"
                @click="openRecord(record)"
            >
                <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div class="flex items-center gap-3 min-w-0">
                        <div class="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-semibold text-sm shrink-0">
                            {{ initials(record.patient && record.patient.name) }}
                        </div>
                        <div class="min-w-0">
                            <div class="font-medium text-gray-800 text-sm truncate">{{ (record.patient && record.patient.name) || 'Paciente sem identificação' }}</div>
                            <div class="text-xs text-gray-400 truncate">{{ (record.patient && (record.patient.email || record.patient.phone || record.patient.document)) || 'Sem dados complementares' }}</div>
                            <div class="mt-1.5 flex flex-wrap items-center gap-2">
                                <span
                                    class="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
                                    :class="matrixBadgeClass(record)"
                                >
                                    {{ matrixBadgeLabel(record) }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="grid gap-3 sm:grid-cols-3 md:min-w-[420px]">
                        <div>
                            <div class="text-[10px] uppercase tracking-wide text-gray-400">Criada em</div>
                            <div class="text-sm text-gray-700 mt-0.5">{{ formatDate(record.created_at) }}</div>
                            <div class="text-xs text-gray-400">{{ formatRelative(record.created_at) }}</div>
                        </div>
                        <div>
                            <div class="text-[10px] uppercase tracking-wide text-gray-400">Última atualização</div>
                            <div class="text-sm text-gray-700 mt-0.5">{{ formatDateTime(record.updated_at) }}</div>
                            <div class="text-xs text-gray-400">{{ formatRelative(record.updated_at) }}</div>
                        </div>
                        <div class="flex items-center sm:justify-end">
                            <div class="flex flex-wrap gap-2 sm:justify-end">
                                <button
                                    type="button"
                                    class="btn btn-sm"
                                    :class="matrixActionButtonClass(record)"
                                    :disabled="!canOpenRecord(record)"
                                    @click.stop="openMatrix(record)"
                                >
                                    {{ matrixActionLabel(record) }}
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-primary"
                                    :disabled="loadingReport || !canOpenRecord(record)"
                                    @click.stop="generateReport(record)"
                                >
                                    PDF
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <PaginationV2 v-show="evaluations.length && !loading" :total="totalEvaluations" :limit="limit" :current="page" item-label="avaliação" @changePage="changePagination"></PaginationV2>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import Swal from 'sweetalert';
import Alert from '@/components/Alert';
import Loading from '@/components/Loading';
import PaginationV2 from '../components/PaginationV2.vue';
import { RecordService, ReportService } from '../services/Entity';
import AuthService from '../services/AuthService';
import { useRouter } from 'vue-router';

const BLANK_REPORT_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

export default {
    components: {
        Loading,
        PaginationV2,
        Alert
    },
    setup() {
        const loading = ref(false);
        const totalEvaluations = ref(0);
        const page = ref(1);
        const limit = ref(10);
        const evaluations = ref([]);
        const searchInput = ref('');
        const activeSearch = ref('');
        const loadingReport = ref(false);
        const router = useRouter();

        onMounted(async () => {
            await loadEvaluations();
        });

        async function loadEvaluations(search) {
            try {
                loading.value = true;
                const filter = search ? { search } : {};
                const { data: { items, total } } = await RecordService.listCompany(page.value, limit.value, filter);

                evaluations.value = items;
                totalEvaluations.value = total;
                loading.value = false;
            } catch(err) {
                loading.value = false;

                Swal({
                    title: 'Ocorreu um problema ao listar avaliações',
                    text: 'Atualize a página e tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
            }
        }

        function applySearch() {
            page.value = 1;
            const term = searchInput.value.trim();
            activeSearch.value = term;
            loadEvaluations(term);
        }

        function clearSearch() {
            searchInput.value = '';
            activeSearch.value = '';
            page.value = 1;
            loadEvaluations();
        }

        async function changePagination(currentPage) {
            page.value = currentPage;
            await loadEvaluations(activeSearch.value);
        }

        function canOpenRecord(record) {
            return Boolean(record && record.uuid && record.patient && record.patient.uuid);
        }

        function hasRiskMatrix(record) {
            return Boolean(record && record.matrix_uuid);
        }

        function matrixBadgeLabel(record) {
            return hasRiskMatrix(record) ? 'Matriz vinculada' : 'Sem matriz de risco';
        }

        function matrixBadgeClass(record) {
            return hasRiskMatrix(record)
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
        }

        function matrixActionLabel(record) {
            return hasRiskMatrix(record) ? 'Ver matriz' : 'Criar matriz';
        }

        function matrixActionButtonClass(record) {
            return hasRiskMatrix(record)
                ? 'border-slate-300 bg-white text-slate-600 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-700'
                : 'border-amber-500 bg-amber-500 text-white hover:border-amber-600 hover:bg-amber-600 disabled:border-amber-300 disabled:bg-amber-300';
        }

        function openRecord(record) {
            if (!canOpenRecord(record)) return;

            router.push({
                name: 'avaliation',
                params: {
                    patient_uuid: record.patient.uuid,
                    record_uuid: record.uuid
                }
            });
        }

        function openMatrix(record) {
            if (!canOpenRecord(record)) return;

            router.push({
                name: 'matrix',
                params: {
                    record_uuid: record.uuid,
                    patient_uuid: record.patient.uuid,
                    matrix_uuid: record.matrix_uuid
                },
                query: { record_id: record.id }
            });
        }

        async function generateReport(record) {
            if (!record || !record.id) return;

            try {
                loadingReport.value = true;

                const response = await ReportService.avaliationReport({
                    record_id: record.id,
                    image: BLANK_REPORT_IMAGE
                }, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                const reportData = response.data;
                const downloadKey = reportData && reportData.key ? reportData.key : null;

                if (downloadKey) {
                    const auth = AuthService.getAuthInfo();
                    const downloadUrl = ReportService.downloadReportUrl(downloadKey);
                    const blobRes = await fetch(downloadUrl, {
                        headers: { 'Authorization': auth && auth.token ? auth.token : '' }
                    });
                    const blob = await blobRes.blob();
                    const blobUrl = URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = `relatorio-avaliacao-${Date.now()}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(blobUrl);
                }
            } catch (err) {
                Swal({
                    title: 'Ocorreu um problema ao gerar o relatório',
                    text: 'Atualize a página e tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
            } finally {
                loadingReport.value = false;
            }
        }

        function initials(name) {
            if (!name) return '?';
            const parts = name.trim().split(' ').filter(Boolean);
            if (!parts.length) return '?';
            if (parts.length === 1) return parts[0][0].toUpperCase();
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }

        function formatDate(dateStr) {
            if (!dateStr) return '—';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '—';
            return date.toLocaleDateString('pt-BR');
        }

        function formatDateTime(dateStr) {
            if (!dateStr) return '—';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '—';
            return date.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }

        function formatRelative(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '';

            const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
            if (diffSec < 60) return 'agora';
            const diffMin = Math.floor(diffSec / 60);
            if (diffMin < 60) return `há ${diffMin} min`;
            const diffHour = Math.floor(diffMin / 60);
            if (diffHour < 24) return `há ${diffHour}h`;
            const diffDay = Math.floor(diffHour / 24);
            if (diffDay < 30) return `há ${diffDay} ${diffDay === 1 ? 'dia' : 'dias'}`;
            const diffMonth = Math.floor(diffDay / 30);
            if (diffMonth < 12) return `há ${diffMonth} ${diffMonth === 1 ? 'mês' : 'meses'}`;
            const diffYear = Math.floor(diffMonth / 12);
            return `há ${diffYear} ${diffYear === 1 ? 'ano' : 'anos'}`;
        }

        return {
            loading,
            loadingReport,
            totalEvaluations,
            page,
            limit,
            evaluations,
            searchInput,
            activeSearch,
            applySearch,
            clearSearch,
            changePagination,
            canOpenRecord,
            matrixBadgeLabel,
            matrixBadgeClass,
            matrixActionLabel,
            matrixActionButtonClass,
            openRecord,
            openMatrix,
            generateReport,
            initials,
            formatDate,
            formatDateTime,
            formatRelative
        };
    }
};
</script>
